import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useBooking } from "../context/BookingContext";
import { useTheme } from "../context/ThemeProvider";
import SingleDatePicker from "../components/SingleDatePicker";

const WidgetPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { updateFormData } = useBooking();
  const { theme } = useTheme();

  // Get hotel and language from URL params
  const hotelId =
    searchParams.get("hotelId") || "ad3b9c1c-42fe-4454-a7ed-257b6a445703";
  const lang = searchParams.get("lang") || "fr";

  // Form state
  const [formData, setFormData] = useState({
    checkIn: undefined as Date | undefined,
    checkOut: undefined as Date | undefined,
    rooms: 1,
    adults: 1,
    children: 0,
    code: "",
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const handleInputChange = (field: string, value: string | number | Date | undefined) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.checkIn) newErrors.checkIn = "Date d'arrivée requise";
    if (!formData.checkOut) newErrors.checkOut = "Date de départ requise";

    if (formData.checkIn && formData.checkOut) {
      const checkInDate = formData.checkIn;
      const checkOutDate = formData.checkOut;
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (checkInDate < today) {
        newErrors.checkIn = "La date d'arrivée ne peut pas être dans le passé";
      }
      if (checkOutDate <= checkInDate) {
        newErrors.checkOut =
          "La date de départ doit être après la date d'arrivée";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    // Update booking context with individual fields
    if (formData.checkIn) updateFormData("checkIn", formData.checkIn);
    if (formData.checkOut) updateFormData("checkOut", formData.checkOut);
    updateFormData("rooms", formData.rooms.toString());
    updateFormData("adults", formData.adults.toString());
    updateFormData("children", formData.children.toString());
    updateFormData("promoCode", formData.code);

    // Build query parameters for /reserve
    const params = new URLSearchParams({
      hotelId,
      lang,
      checkIn: formData.checkIn ? formData.checkIn.toISOString().split('T')[0] : "",
      checkOut: formData.checkOut ? formData.checkOut.toISOString().split('T')[0] : "",
      rooms: formData.rooms.toString(),
      adults: formData.adults.toString(),
      children: formData.children.toString(),
    });

    if (formData.code) {
      params.append("code", formData.code);
    }

    // Check if we're in an iframe
    if (window.parent !== window) {
      // We're in an iframe - redirect parent window
      window.parent.location.href = `${
        window.location.origin
      }/hotel-adriatica/reservation?${params.toString()}`;
    } else {
      // We're not in an iframe - navigate directly
      navigate(`/hotel-adriatica/reservation?${params.toString()}`);
    }
  };


  return (
    <div className="min-h-screen bg-inherit flex items-center justify-center p-4">
      <div className="p-8 w-full max-w-5xl  ">
        {/* Search Form */}
        <form onSubmit={handleSubmit} className="space-y-8 widget-form">
          {/* First Row - All 5 fields */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {/* Arrival Date */}
            <div>
              <label className="block text-sm font-bold text-gray-800 mb-2 uppercase tracking-wide">
                {lang === "fr" ? "DATE D'ARRIVÉE" : "CHECK-IN"}
              </label>
              <SingleDatePicker
                selectedDate={formData.checkIn}
                onDateChange={(date) => handleInputChange("checkIn", date)}
                error={errors.checkIn}
                minDate={new Date()}
              />
              {errors.checkIn && (
                <p className="text-red-500 text-xs mt-1">{errors.checkIn}</p>
              )}
            </div>

            {/* Departure Date */}
            <div>
              <label className="block text-sm font-bold text-gray-800 mb-2 uppercase tracking-wide">
                {lang === "fr" ? "DATE DE DÉPART" : "CHECK-OUT"}
              </label>
              <SingleDatePicker
                selectedDate={formData.checkOut}
     
                                 onDateChange={(date) => handleInputChange("checkOut", date)}
                error={errors.checkOut}
                minDate={formData.checkIn || new Date()}
              />
              {errors.checkOut && (
                <p className="text-red-500 text-xs mt-1">{errors.checkOut}</p>
              )}
            </div>

            {/* Rooms */}
            <div className="widget-select-wrapper">
              <label className="block text-sm font-bold text-gray-800 mb-2 uppercase tracking-wide">
                {lang === "fr" ? "CHAMBRES" : "ROOMS"}
              </label>
              <select
                value={formData.rooms}
                onChange={(e) =>
                  handleInputChange("rooms", parseInt(e.target.value))
                }
                className="w-full py-3 widget-bottom-border text-gray-800 cursor-pointer"
              >
                {[1, 2, 3, 4, 5].map((num) => (
                  <option key={num} value={num}>
                    {num} {lang === "fr" ? "Chambre" : "Room"}
                    {num > 1 ? "s" : ""}
                  </option>
                ))}
              </select>
            </div>

            {/* Adults */}
            <div className="widget-select-wrapper">
              <label className="block text-sm font-bold text-gray-800 mb-2 uppercase tracking-wide">
                {lang === "fr" ? "ADULTES" : "ADULTS"}
              </label>
              <select
                value={formData.adults}
                onChange={(e) =>
                  handleInputChange("adults", parseInt(e.target.value))
                }
                className="w-full py-3 widget-bottom-border text-gray-800 cursor-pointer"
              >
                {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                  <option key={num} value={num}>
                    {num} {lang === "fr" ? "Adulte" : "Adult"}
                    {num > 1 ? "s" : ""}
                  </option>
                ))}
              </select>
            </div>

            {/* Children */}
            <div className="widget-select-wrapper">
              <label className="block text-sm font-bold text-gray-800 mb-2 uppercase tracking-wide">
                {lang === "fr" ? "ENFANTS" : "CHILDREN"}
              </label>
              <select
                value={formData.children}
                onChange={(e) =>
                  handleInputChange("children", parseInt(e.target.value))
                }
                className="w-full py-3 widget-bottom-border text-gray-800 cursor-pointer"
              >
                {[0, 1, 2, 3, 4, 5].map((num) => (
                  <option key={num} value={num}>
                    {num} {lang === "fr" ? "Enfant" : "Child"}
                    {num > 1 ? "s" : ""}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Second Row - Code + Button */}
          <div className="flex items-end gap-6">
            {/* Promo Code - Full width until button */}
            <div className="flex-1">
              <label className="block text-sm font-bold text-gray-800 mb-2 uppercase tracking-wide">
                {lang === "fr" ? "CODE" : "CODE"}
              </label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => handleInputChange("code", e.target.value)}
                placeholder={
                  lang === "fr" ? "Entrez votre code" : "Enter your code"
                }
                className="w-full py-3 widget-bottom-border text-gray-800 placeholder-gray-400"
              />
            </div>

            {/* Submit Button */}
            <div className="flex-shrink-0">
              <button
                type="submit"
                className="px-8 py-3 bg-[#87764e] hover:bg-[#7a6b47] text-white font-bold text-lg rounded-lg transition-colors duration-200 shadow-lg hover:shadow-xl"
                style={{
                  backgroundColor: theme.primaryColor || "#87764e",
                }}
              >
                {lang === "fr" ? "RÉSERVER" : "BOOK NOW"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WidgetPage;
